/**
 * The queue, as React state — spec 10 §3 and §4.
 *
 * `useSyncExternalStore` rather than `useState` plus an effect, for one reason that matters
 * here: it re-reads the store during render, so there is no window in which a screen paints
 * a status the queue has already moved past. The chip that must never lie (gate G-18) is
 * therefore never a frame behind the queue it describes.
 *
 * The snapshot is recomputed on every notification and is a fresh object each time, so the
 * cached-snapshot rule of `useSyncExternalStore` is honoured by holding the last one and
 * only replacing it when the queue actually told us something changed.
 */

import { useSyncExternalStore } from "react";

import { queueStatus, subscribeQueue, type QueueStatus } from "../api/client.js";

let snapshot: QueueStatus = queueStatus();
let dirty = true;

function subscribe(listener: () => void): () => void {
  // A new subscriber has not seen anything yet, and the queue may have been loaded — or
  // reconfigured by the suite — while nobody was listening. Never start from a cached
  // snapshot taken before this mount: that is precisely how a green chip outlives its queue.
  dirty = true;
  return subscribeQueue(() => {
    dirty = true;
    listener();
  });
}

function read(): QueueStatus {
  if (dirty) {
    snapshot = queueStatus();
    dirty = false;
  }
  return snapshot;
}

export function useQueueStatus(): QueueStatus {
  return useSyncExternalStore(subscribe, read, read);
}

/**
 * Mark the cached snapshot stale.
 *
 * The suite reconfigures the queue between tests, which resets module state without going
 * through a notification; without this the first render of the next test would be handed the
 * previous test's snapshot.
 */
export function invalidateQueueSnapshot(): void {
  dirty = true;
}
